import {
  doc,
  collection,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore'
import { db, getActiveDb, getActiveAuth } from '../config/firebase'
import { transformKeysToSnake, transformKeysToCamel } from '../utils/namingConventions'
import type {
  ProfileAccessDocument,
  ProfileAccessIndex,
  ProfileRole,
  ProfilePermissions,
  ActivityLogEntry,
  CollaborationStats
} from '../types/account'
import type { PermissionLevel } from '../hooks/useProfileAccess'
import { ROLE_PERMISSIONS } from '../config/collaboration'
import { API_BASE_URL } from '../config/api'

export class ProfileAccessService {
  // Get the active database instance (default or view-as)
  private getDb() {
    return getActiveDb()
  }
  
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const activeAuth = getActiveAuth()
    if (!activeAuth.currentUser) {
      throw new Error('User must be authenticated')
    }
    const token = await activeAuth.currentUser.getIdToken()
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  }

  private async apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers = await this.getAuthHeaders()
    const res = await fetch(`${API_BASE_URL}/api/v1${endpoint}`, {
      ...options,
      headers: { ...headers, ...(options.headers || {}) }
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.detail || `HTTP ${res.status}: ${res.statusText}`)
    }
    return res.json()
  }
  
  /**
   * Grant access to a profile
   */
  async grantAccess(
    profileId: string,
    targetUserId: string,
    role: ProfileRole,
    grantedBy?: string,
    notes?: string,
    relationship?: string,
    otherRelationship?: string,
    userName?: string,
    userEmail?: string
  ): Promise<void> {
    const activeAuth = getActiveAuth()
    if (!activeAuth.currentUser) {
      throw new Error('User must be authenticated')
    }

    const currentUserId = grantedBy || activeAuth.currentUser.uid

    // Verify the granter has permission to manage access
    const granterAccess = await this.getUserProfileAccess(profileId, currentUserId)
    if (!granterAccess || !granterAccess.permissions.manageAccess) {
      throw new Error('Insufficient permissions to grant access')
    }

    // Prevent granting owner role (only one owner per profile)
    if (role === 'owner') {
      throw new Error('Cannot grant owner role')
    }

    // accountability_partner is internal-only — set solely by the partnership
    // accept/end lifecycle, never through the manual grant-access flow.
    if (role === 'accountability_partner') {
      throw new Error('This role can only be granted via the partnership flow')
    }

    // Use backend endpoint for authoritative access grant
    await this.apiCall(`/profiles/${profileId}/access/grant`, {
      method: 'POST',
      body: JSON.stringify({
        profileId: profileId,
        targetUserId: targetUserId,
        role,
        notes,
        relationship,
        otherRelationship: otherRelationship,
        userName: userName,
        userEmail: userEmail
      })
    })
  }

  /**
   * Revoke access to a profile
   */
  async revokeAccess(profileId: string, targetUserId: string): Promise<void> {
    const activeAuth = getActiveAuth()
    if (!activeAuth.currentUser) {
      throw new Error('User must be authenticated')
    }

    const currentUserId = activeAuth.currentUser.uid

    // Verify the revoker has permission to manage access
    const revokerAccess = await this.getUserProfileAccess(profileId, currentUserId)
    if (!revokerAccess || !revokerAccess.permissions.manageAccess) {
      throw new Error('Insufficient permissions to revoke access')
    }

    // Cannot revoke owner's access
    const targetAccess = await this.getUserProfileAccess(profileId, targetUserId)
    if (targetAccess?.role === 'owner') {
      throw new Error('Cannot revoke owner access')
    }

    // Backend authoritative revoke
    await this.apiCall(`/profiles/${profileId}/access/revoke/${targetUserId}`, {
      method: 'POST'
    })
  }

  /**
   * Remove own access to a shared profile
   */
  async removeOwnAccess(profileId: string): Promise<void> {
    const activeAuth = getActiveAuth()
    if (!activeAuth.currentUser) {
      throw new Error('User must be authenticated')
    }

    const userId = activeAuth.currentUser.uid

    // Cannot remove owner's own access
    const userAccess = await this.getUserProfileAccess(profileId, userId)
    if (userAccess?.role === 'owner') {
      throw new Error('Cannot remove your own access as owner')
    }

    return await runTransaction(this.getDb(), async (transaction) => {
      // Remove from profile's access collection
      const accessRef = doc(this.getDb(), 'profiles', profileId, 'access', userId)
      transaction.delete(accessRef)

      // Remove from user's profile access index
      const userIndexRef = doc(this.getDb(), 'accounts', userId, 'profile_access', profileId)
      transaction.delete(userIndexRef)
    })
  }

  /**
   * Update user's role for a profile
   */
  async updateUserRole(
    profileId: string,
    targetUserId: string,
    newRole: ProfileRole
  ): Promise<void> {
    const activeAuth = getActiveAuth()
    if (!activeAuth.currentUser) {
      throw new Error('User must be authenticated')
    }

    const currentUserId = activeAuth.currentUser.uid

    // Verify permissions
    const currentUserAccess = await this.getUserProfileAccess(profileId, currentUserId)
    if (!currentUserAccess || !currentUserAccess.permissions.manageAccess) {
      throw new Error('Insufficient permissions to update role')
    }

    if (newRole === 'owner') {
      throw new Error('Cannot grant owner role')
    }

    // accountability_partner is internal-only — set solely by the partnership
    // accept/end lifecycle, never through the manual role-update flow.
    if (newRole === 'accountability_partner') {
      throw new Error('This role can only be granted via the partnership flow')
    }

    await this.apiCall(`/profiles/${profileId}/access/role`, {
      method: 'POST',
      body: JSON.stringify({
        profileId: profileId,
        targetUserId: targetUserId,
        newRole: newRole
      })
    })
  }

  /**
   * Get user's access to a specific profile
   */
  async getUserProfileAccess(profileId: string, userId: string): Promise<ProfileAccessDocument | null> {
    try {
      const db = this.getDb()
      const accessRef = doc(db, `profiles/${profileId}/access`, userId)
      const accessSnap = await getDoc(accessRef)
      
      if (!accessSnap.exists()) {
        return null
      }

      const data = transformKeysToCamel(accessSnap.data()) as any
      return {
        ...data,
        grantedAt: data.grantedAt?.toDate?.() || data.grantedAt,
        expiresAt: data.expiresAt?.toDate?.() || data.expiresAt
      } as ProfileAccessDocument
    } catch (error) {
      console.error('Error getting user profile access:', error)
      return null
    }
  }

  /**
   * Get a single collaborator with full details (including user info)
   * More efficient than getProfileCollaborators when you only need one collaborator
   */
  async getProfileCollaborator(profileId: string, userId: string): Promise<(ProfileAccessDocument & { userId: string }) | null> {
    try {
      const db = this.getDb()
      const accessRef = doc(db, `profiles/${profileId}/access`, userId)
      const accessSnap = await getDoc(accessRef)
      
      if (!accessSnap.exists()) {
        return null
      }

      const raw = accessSnap.data()
      const data = transformKeysToCamel(raw) as any
      
      // Get user info to populate userName and userEmail
      const userInfo = await this.getUserInfo(userId)
      
      return {
        userId: accessSnap.id,
        ...data,
        userName: userInfo?.name || data.userName,
        userEmail: userInfo?.email || data.userEmail,
        grantedAt: data.grantedAt?.toDate?.() || data.grantedAt,
        expiresAt: data.expiresAt?.toDate?.() || data.expiresAt
      } as ProfileAccessDocument & { userId: string }
    } catch (error) {
      console.error('Error getting profile collaborator:', error)
      return null
    }
  }

  /**
   * Resolve a permission value (string or object) to a PermissionLevel string
   */
  private resolvePermissionLevel(value: any): PermissionLevel | undefined {
    if (!value) return undefined
    
    // If it's already a string, map it if necessary
    if (typeof value === 'string') {
        if (value === 'view_only') return 'view'
        if (value === 'full_control') return 'full_control'
        if (value === 'none' || value === 'view' || value === 'edit') return value as PermissionLevel
        return 'view' // Default fallback for unknown strings that aren't 'none'
    }
    
    // If it's an object, look for the highest permission
    if (typeof value === 'object') {
        const values = Object.values(value)
        if (values.includes('full_control')) return 'full_control'
        if (values.includes('edit')) return 'edit'
        if (values.includes('view_only') || values.includes('view')) return 'view'
        return 'none'
    }
    
    return undefined
  }

  /**
   * Get comprehensive access details for a user on a profile
   * Returns formatted permissions including granular permissions and record restrictions
   */
  async getUserAccessDetails(profileId: string, userId: string): Promise<{
    role: ProfileRole
    permissions: ProfilePermissions
    granularPermissions?: {
      health_records?: PermissionLevel
      wellness_goals?: PermissionLevel
      genetic_reports?: PermissionLevel
      documents?: PermissionLevel
      wellness_coach?: PermissionLevel
      gene_chat?: PermissionLevel
    }
    recordRestrictions?: Record<string, string[]> | { collection: string; record_ids: string[] }
    grantedAt?: Date
    expiresAt?: Date | null
    status?: 'active' | 'pending' | 'revoked' | 'expired'
    isOwner?: boolean
  }> {
    try {
      // First check if user is the owner
      const profileRef = doc(this.getDb(), 'profiles', profileId)
      const profileSnap = await getDoc(profileRef)
      
      if (profileSnap.exists()) {
        const profileData = profileSnap.data()
        const ownerUid = profileData.owner_uid || profileData.ownerUid
        
        if (ownerUid === userId) {
          // User is the owner - return owner permissions
          // Helper function to safely convert Firestore timestamp to Date
          const toDateSafe = (v: any): Date | undefined => {
            if (!v) return undefined
            if (typeof v.toDate === 'function') {
              const date = v.toDate()
              return date instanceof Date && !isNaN(date.getTime()) ? date : undefined
            }
            if (v instanceof Date) {
              return !isNaN(v.getTime()) ? v : undefined
            }
            if (typeof v === 'object' && typeof v.seconds === 'number') {
              const date = new Date(v.seconds * 1000 + (v.nanoseconds || 0) / 1000000)
              return !isNaN(date.getTime()) ? date : undefined
            }
            if (typeof v === 'string') {
              const date = new Date(v)
              return !isNaN(date.getTime()) ? date : undefined
            }
            if (typeof v === 'number') {
              const date = new Date(v)
              return !isNaN(date.getTime()) ? date : undefined
            }
            return undefined
          }
          
          // Get profile creation date if available
          const createdAt = toDateSafe(profileData.created_at || profileData.createdAt)
          
          return {
            role: 'owner',
            permissions: ROLE_PERMISSIONS.owner,
            granularPermissions: {
              health_records: 'full_control',
              wellness_goals: 'full_control',
              genetic_reports: 'full_control',
              documents: 'full_control',
              wellness_coach: 'full_control',
              gene_chat: 'full_control' // Backward compatibility
            },
            isOwner: true,
            status: 'active',
            grantedAt: createdAt
          }
        }
      }

      // User is not the owner - fetch from access subcollection
      const db = this.getDb()
      const accessRef = doc(db, `profiles/${profileId}/access`, userId)
      const accessSnap = await getDoc(accessRef)
      
      if (!accessSnap.exists()) {
        throw new Error('Access not found')
      }

      const raw = accessSnap.data()
      const data = transformKeysToCamel(raw) as any

      // Check if access is expired
      let status: 'active' | 'pending' | 'revoked' | 'expired' = 'active'
      if (data.status === 'revoked') {
        status = 'revoked'
      } else if (data.expiresAt) {
        const expiresAt = data.expiresAt?.toDate?.() || data.expiresAt
        if (expiresAt && expiresAt < new Date()) {
          status = 'expired'
        }
      }

      // Helper function to safely convert Firestore timestamp to Date
      const toDateSafe = (v: any): Date | undefined => {
        if (!v) return undefined
        // Handle Firestore Timestamp with toDate method
        if (typeof v.toDate === 'function') {
          const date = v.toDate()
          return date instanceof Date && !isNaN(date.getTime()) ? date : undefined
        }
        // Handle Date object
        if (v instanceof Date) {
          return !isNaN(v.getTime()) ? v : undefined
        }
        // Handle serialized Firestore Timestamp (object with seconds/nanoseconds)
        if (typeof v === 'object' && typeof v.seconds === 'number') {
          const date = new Date(v.seconds * 1000 + (v.nanoseconds || 0) / 1000000)
          return !isNaN(date.getTime()) ? date : undefined
        }
        // Handle string timestamp
        if (typeof v === 'string') {
          const date = new Date(v)
          return !isNaN(date.getTime()) ? date : undefined
        }
        // Handle number timestamp (milliseconds)
        if (typeof v === 'number') {
          const date = new Date(v)
          return !isNaN(date.getTime()) ? date : undefined
        }
        return undefined
      }

      // Extract granular permissions
      const granularPermissions = data.granularPermissions || data.granular_permissions

      // Extract record restrictions (handle both camelCase and snake_case)
      const recordRestrictions = data.recordRestrictions || data.record_restrictions

      // Convert timestamps safely
      const grantedAt = toDateSafe(data.grantedAt || data.granted_at)
      let expiresAt = toDateSafe(data.expiresAt || data.expires_at)
      
      // If expiresAt is not in access document, check the share document
      // This handles cases where shares were accepted before expires_at was copied to access docs
      if (!expiresAt) {
        try {
          const sourceId = data.source_id || data.sourceId
          let shareData: any = null
          
          // First try to find share by source_id if available
          if (sourceId) {
            const shareRef = doc(this.getDb(), 'shared_profiles', sourceId)
            const shareSnap = await getDoc(shareRef)
            if (shareSnap.exists()) {
              shareData = shareSnap.data()
            }
          }
          
          // If not found by source_id, try querying by profile_id and shared_user_id
          if (!shareData) {
            const sharesQuery = query(
              collection(this.getDb(), 'shared_profiles'),
              where('profile_id', '==', profileId),
              where('shared_user_id', '==', userId),
              where('status', 'in', ['pending', 'accepted'])
            )
            const sharesSnapshot = await getDocs(sharesQuery)
            if (!sharesSnapshot.empty) {
              shareData = sharesSnapshot.docs[0].data()
            }
          }
          
          if (shareData) {
            const shareExpiresAt = toDateSafe(shareData.expires_at || shareData.expiresAt)
            if (shareExpiresAt) {
              expiresAt = shareExpiresAt
            }
          }
        } catch (shareError) {
          // Non-blocking - if we can't find the share, just use what we have
          console.debug('Could not fetch share document for expires_at:', shareError)
        }
      }

      const resolvedGranular = granularPermissions ? {
        health_records: this.resolvePermissionLevel(granularPermissions.health_records || granularPermissions.healthRecords),
        wellness_goals: this.resolvePermissionLevel(granularPermissions.wellness_goals || granularPermissions.wellnessGoals),
        genetic_reports: this.resolvePermissionLevel(granularPermissions.genetic_reports || granularPermissions.geneticReports),
        documents: this.resolvePermissionLevel(granularPermissions.documents),
        wellness_coach: this.resolvePermissionLevel(granularPermissions.wellness_coach || granularPermissions.wellnessCoach || granularPermissions.gene_chat || granularPermissions.geneChat),
        gene_chat: this.resolvePermissionLevel(granularPermissions.gene_chat || granularPermissions.geneChat) // Backward compatibility
      } : undefined

      let permissions = data.permissions || ROLE_PERMISSIONS.viewer
      
      // If we have granular permissions, they should override role-based defaults
      if (resolvedGranular) {
        permissions = { ...permissions }
        // If genetic reports are explicitly set to none, disable viewReports
        if (resolvedGranular.genetic_reports === 'none') {
          permissions.viewReports = false
        } else if (resolvedGranular.genetic_reports) {
          // If explicitly set to something else, ensure viewReports is true
          permissions.viewReports = true
        }
      }

      return {
        role: (data.role || 'viewer') as ProfileRole,
        permissions: permissions,
        granularPermissions: resolvedGranular,
        recordRestrictions,
        grantedAt,
        expiresAt: expiresAt || null,
        status,
        isOwner: false
      }
    } catch (error) {
      console.error('Error getting user access details:', error)
      throw error
    }
  }

  /**
   * Get comprehensive audit log for a profile
   */
  async getProfileAuditLog(profileId: string, limitCount: number = 100): Promise<ActivityLogEntry[]> {
    try {
      const auditRef = collection(this.getDb(), `profiles/${profileId}/activity_log`)
      const q = query(auditRef, orderBy('timestamp', 'desc'), limit(limitCount))
      const snapshot = await getDocs(q)
      
      return snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          ...data,
          timestamp: data.timestamp?.toDate?.() || data.timestamp
        } as ActivityLogEntry
      })
    } catch (error) {
      console.error('Error getting profile audit log:', error)
      return []
    }
  }

  /**
   * Get user's access history across all profiles
   */
  async getUserAccessHistory(userId: string, limitCount: number = 100): Promise<ActivityLogEntry[]> {
    try {
      const userAccessRef = collection(this.getDb(), `accounts/${userId}/profile_access`)
      const userAccessSnapshot = await getDocs(userAccessRef)
      
      const profileIds = userAccessSnapshot.docs.map(doc => doc.id)
      const allAuditLogs: ActivityLogEntry[] = []
      
      // Get audit logs for each profile the user has access to
      for (const profileId of profileIds) {
        const profileAuditLogs = await this.getProfileAuditLog(profileId, 50)
        const userRelevantLogs = profileAuditLogs.filter(log => 
          log.userId === userId || 
          log.action === 'access_revoked'
        )
        allAuditLogs.push(...userRelevantLogs)
      }
      
      // Sort by timestamp and limit results
      return allAuditLogs
        .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        .slice(0, limitCount)
    } catch (error) {
      console.error('Error getting user access history:', error)
      return []
    }
  }

  /**
   * Get all profiles accessible to a user
   */
  async getUserAccessibleProfiles(userId: string): Promise<ProfileAccessIndex[]> {
    try {
      const accessSnapshot = await getDocs(
        collection(this.getDb(), `accounts/${userId}/profile_access`)
      )

      // Fetch profile metadata for each access index if missing
      const profilesWithData = await Promise.all(
        accessSnapshot.docs.map(async (d) => {
          const raw = d.data()
          const data = transformKeysToCamel(raw) as any
          
          // Check if metadata is missing and fetch it
          let profileName = data.profileName
          let profileUsername = data.profileUsername
          let ownerName = data.ownerName
          let ownerUid = data.ownerUid
          let accessGrantedAt = data.accessGrantedAt?.toDate?.() || data.accessGrantedAt
          
          // If metadata is missing, fetch from profile document
          if (!profileName || !profileUsername || !ownerName || !accessGrantedAt) {
            try {
              // Fetch from profile document
              const profileRef = doc(this.getDb(), 'profiles', data.profileId)
              const profileSnap = await getDoc(profileRef)
              
              if (profileSnap.exists()) {
                const profileData = profileSnap.data()
                profileName = profileData.name || profileName
                profileUsername = profileData.username || profileUsername
                ownerUid = profileData.owner_uid || profileData.ownerUid || ownerUid
                // Owner name is denormalised onto the profile_access index when
                // access is granted; another user's account document is not readable.
                ownerName = ownerName || profileData.owner_name || profileData.ownerName
              }
              
              // If accessGrantedAt is missing, fetch from profile's access document
              if (!accessGrantedAt) {
                const profileAccessRef = doc(this.getDb(), 'profiles', data.profileId, 'access', userId)
                const profileAccessSnap = await getDoc(profileAccessRef)
                if (profileAccessSnap.exists()) {
                  const accessData = profileAccessSnap.data()
                  
                  // Try multiple field names and handle both Firestore Timestamp and Date objects
                  const timestampField = accessData.grantedAt || accessData.granted_at || 
                                        accessData.createdAt || accessData.created_at ||
                                        accessData.created || accessData.timestamp
                  
                  if (timestampField) {
                    // Handle Firestore Timestamp
                    if (typeof timestampField.toDate === 'function') {
                      accessGrantedAt = timestampField.toDate()
                    } 
                    // Handle Date object
                    else if (timestampField instanceof Date) {
                      accessGrantedAt = timestampField
                    }
                    // Handle timestamp number
                    else if (typeof timestampField === 'number') {
                      accessGrantedAt = new Date(timestampField)
                    }
                    // Handle timestamp string
                    else if (typeof timestampField === 'string') {
                      accessGrantedAt = new Date(timestampField)
                    }
                  }
                }
              }
            } catch (fetchError) {
              console.warn('Failed to fetch profile metadata:', fetchError)
            }
          }
          
          return {
            ...data,
            profileName,
            profileUsername,
            ownerName,
            ownerUid,
            accessGrantedAt,
            lastAccessed: data.lastAccessed?.toDate?.() || data.lastAccessed
          } as ProfileAccessIndex
        })
      )

      // Sort by last_accessed descending (most recently accessed first), fallback to access_granted_at
      profilesWithData.sort((a, b) => {
        const aLastAccessed = a.lastAccessed
        const bLastAccessed = b.lastAccessed
        
        // If both have lastAccessed, sort by that
        if (aLastAccessed && bLastAccessed) {
          const aTime = aLastAccessed instanceof Date ? aLastAccessed.getTime() : new Date(aLastAccessed).getTime()
          const bTime = bLastAccessed instanceof Date ? bLastAccessed.getTime() : new Date(bLastAccessed).getTime()
          return bTime - aTime // Descending (newest first)
        }
        
        // If only one has lastAccessed, prioritize it
        if (aLastAccessed && !bLastAccessed) return -1
        if (!aLastAccessed && bLastAccessed) return 1
        
        // Fallback to access_granted_at sorting
        const aDate = a.accessGrantedAt
        const bDate = b.accessGrantedAt

        // Handle missing dates - put them at the end
        if (!aDate && !bDate) return 0
        if (!aDate) return 1
        if (!bDate) return -1

        // Convert to timestamp for comparison
        const aTime = aDate instanceof Date ? aDate.getTime() : new Date(aDate).getTime()
        const bTime = bDate instanceof Date ? bDate.getTime() : new Date(bDate).getTime()

        // Sort descending (newest first)
        return bTime - aTime
      })

      return profilesWithData
    } catch (error) {
      // Firestore rules deny the list during view-as mode auth transitions — expected, not a bug.
      // Log at error level in all cases so silent failures are visible in devtools.
      if (error instanceof Error && error.message.includes("false for 'list'")) {
        console.error(
          'ProfileAccessService: Firestore rules denied profile_access list — ' +
          'check deployed firestore.rules. Returning empty profile list.',
          error
        )
        return []
      }
      console.error('ProfileAccessService: getUserAccessibleProfiles failed:', error)
      return []
    }
  }

  /**
   * Get all users with access to a profile
   */
  async getProfileCollaborators(profileId: string): Promise<Array<ProfileAccessDocument & { userId: string }>> {
    try {
      const db = this.getDb()
      const accessSnapshot = await getDocs(
        collection(db, `profiles/${profileId}/access`)
      )

      const collaborators = await Promise.all(
        accessSnapshot.docs.map(async (d) => {
          const raw = d.data()
          const data = transformKeysToCamel(raw) as any
          
          // Get user info to populate userName and userEmail
          const userInfo = await this.getUserInfo(d.id)
          
          return {
            userId: d.id,
            ...data,
            userName: userInfo?.name || data.userName,
            userEmail: userInfo?.email || data.userEmail,
            grantedAt: data.grantedAt?.toDate?.() || data.grantedAt,
            expiresAt: data.expiresAt?.toDate?.() || data.expiresAt
          } as ProfileAccessDocument & { userId: string }
        })
      )

      return collaborators
    } catch (error) {
      // A view-only collaborator (e.g. an accountability partner) is
      // expected to be denied listing the full access collection —
      // firestore.rules only grants that to the owner or a collaborator
      // with manageAccess. That's working as intended, not a failure, so
      // log it quietly rather than as an error; anything else still
      // surfaces loudly.
      const isPermissionDenied = (error as { code?: string })?.code === 'permission-denied'
      if (isPermissionDenied) {
        console.debug('getProfileCollaborators: caller lacks manageAccess, returning empty list')
      } else {
        console.error('Error getting profile collaborators:', error)
      }
      return []
    }
  }

  /**
   * Get total collaborators count for a profile (owner included)
   */
  async getCollaboratorsCount(profileId: string): Promise<number> {
    try {
      const db = this.getDb()
      const accessSnapshot = await getDocs(
        collection(db, `profiles/${profileId}/access`)
      )
      return accessSnapshot.size
    } catch (error) {
      // Handle permission errors gracefully (e.g., in view-as mode)
      if (error instanceof Error && (
        error.message.includes("false for 'list'") ||
        error.message.includes('permission-denied') ||
        error.message.includes('Permission denied')
      )) {
        console.warn('Permission denied getting collaborators count (likely in view-as mode):', profileId)
        return 1 // Return 1 (owner only) as safe default
      }
      console.error('Error getting collaborators count:', error)
      return 0
    }
  }

  /**
   * Check if user has specific permission for a profile
   */
  async hasProfilePermission(
    profileId: string,
    userId: string,
    permission: keyof typeof ROLE_PERMISSIONS.owner
  ): Promise<boolean> {
    const access = await this.getUserProfileAccess(profileId, userId)
    return access ? access.permissions[permission] : false
  }

  /**
   * Update last accessed time for a profile
   */
  async updateLastAccessed(profileId: string, userId: string): Promise<void> {
    try {
      const db = this.getDb()
      const indexRef = doc(db, `accounts/${userId}/profile_access`, profileId)
      await updateDoc(indexRef, transformKeysToSnake({
        lastAccessed: serverTimestamp()
      }))
    } catch (error) {
      console.error('Error updating last accessed:', error)
    }
  }

  /**
   * Get collaboration statistics for a user
   */
  async getCollaborationStats(userId: string): Promise<CollaborationStats> {
    try {
      const db = this.getDb()
      const [ownedProfiles, accessibleProfiles] = await Promise.all([
        getDocs(query(collection(db, 'profiles'), where('owner_uid', '==', userId))),
        getDocs(collection(db, `accounts/${userId}/profile_access`))
      ])

      const profilesOwned = ownedProfiles.size
      const profilesSharedWithMe = accessibleProfiles.docs.filter(doc => {
        const data = doc.data()
        // Additional safety check: exclude profiles where user is owner
        return data.owner_uid !== userId && data.ownerUid !== userId
      }).length

      // Count profiles this user shares with others
      let profilesIShare = 0
      for (const profileDoc of ownedProfiles.docs) {
        try {
          const collaborators = await getDocs(
            collection(db, `profiles/${profileDoc.id}/access`)
          )
          if (collaborators.size > 1) { // More than just the owner
            profilesIShare++
          }
        } catch (error) {
          // Skip profiles where we don't have permission to read access (e.g., view-as mode)
          if (error instanceof Error && error.message.includes("false for 'list'")) {
            continue
          }
          throw error
        }
      }

      return {
        profilesOwned: profilesOwned,
        profilesSharedWithMe: profilesSharedWithMe,
        profilesIShare: profilesIShare,
        pendingInvitations: 0, // Will be implemented in ShareInvitationService
        activeCollaborators: accessibleProfiles.size
      }
    } catch (error) {
      console.error('Error getting collaboration stats:', error)
      return {
        profilesOwned: 0,
        profilesSharedWithMe: 0,
        profilesIShare: 0,
        pendingInvitations: 0,
        activeCollaborators: 0
      }
    }
  }

  /**
   * Log activity for audit trail
   */
  private async logActivity(profileId: string, activity: Omit<ActivityLogEntry, 'id'>): Promise<void> {
    try {
      const db = this.getDb()
      const activityRef = doc(collection(db, `profiles/${profileId}/activity_log`))
      await setDoc(activityRef, transformKeysToSnake({
        ...activity,
        timestamp: serverTimestamp()
      }))
    } catch (error) {
      console.error('Error logging activity:', error)
    }
  }

  /**
   * Get user information
   */
  private async getUserInfo(userId: string): Promise<{ name: string; email?: string } | null> {
    try {
      // Security rules only allow reading your own account document.
      // Other users' names/emails come from the access document itself
      // (userName / userEmail are written when access is granted).
      if (getActiveAuth().currentUser?.uid !== userId) return null
      const db = this.getDb()
      const userRef = doc(db, 'accounts', userId)
      const userSnap = await getDoc(userRef)
      if (!userSnap.exists()) return null
      
      const userData = userSnap.data()
      return { 
        name: userData.name,
        email: userData.email
      }
    } catch (error) {
      console.error('Error getting user info:', error)
      return null
    }
  }

  /**
   * Get user email from Firebase Auth
   */
  async getUserEmail(userId: string): Promise<string | null> {
    try {
      // Try to get user info from Firebase Auth
      const user = getActiveAuth().currentUser
      if (user && user.uid === userId) {
        return user.email
      }
      
      // If not current user, try to get from accounts collection
      const db = this.getDb()
      const accountRef = doc(db, 'accounts', userId)
      const accountSnap = await getDoc(accountRef)
      
      if (accountSnap.exists()) {
        const accountData = accountSnap.data()
        return accountData.email || null
      }
      
      return null
    } catch (error) {
      console.error('Error getting user email:', error)
      return null
    }
  }

  /**
   * Create owner access when profile is created
   */
  async createOwnerAccess(profileId: string, ownerId: string, profileData: any): Promise<void> {
    const db = this.getDb()
    return await runTransaction(db, async (transaction) => {
      // Create owner access document
      const accessRef = doc(db, `profiles/${profileId}/access`, ownerId)
      const accessData: ProfileAccessDocument = {
        role: 'owner',
        permissions: ROLE_PERMISSIONS.owner,
        grantedBy: ownerId,
        grantedAt: new Date(),
        status: 'active'
      }
      
      transaction.set(accessRef, accessData)

      // Create owner's access index
      const indexRef = doc(db, `accounts/${ownerId}/profile_access`, profileId)
      const indexData: ProfileAccessIndex = {
        profileId: profileId,
        profileName: profileData.name,
        profileUsername: profileData.username,
        ownerName: profileData.ownerName || 'Unknown',
        ownerUid: ownerId,
        role: 'owner',
        accessGrantedAt: new Date(),
        lastAccessed: new Date(),
        profilePhoto: profileData.photo
      }
      
      transaction.set(indexRef, transformKeysToSnake(indexData))
    })
  }
}

// Export singleton instance
export const profileAccessService = new ProfileAccessService()
